/**
 * Copyright (C) Siemens AG 1996-2025 ALL RIGHTS RESERVED.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import {CommonModule, Location} from '@angular/common';
import {PassportService} from '../../../../../services/passport.service';
import {catchError, firstValueFrom, of, Subscription} from 'rxjs';
import {TextInputComponent} from './components/text-input/text-input.component';
import {IxModule} from '@siemens/ix-angular';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {AbstractControl, ReactiveFormsModule} from '@angular/forms';
import {datadogLogs} from '@datadog/browser-logs';
import {
  FormCategory,
  FormFieldAttribute,
  FormPurpose,
  FormSubCategory,
} from '../../../../../models/FormCategory';
import {EnumInputComponent} from './components/enum-input/enum-input.component';
import {NumberInputComponent} from './components/number-input/number-input.component';
import {DateInputComponent} from './components/date-input/date-input.component';
import {BatteryPassAttributeLabel} from '../../../../../models/BatteryPassAttributeLabel';
import {DocumentInputComponent} from './components/document-input/document-input.component';
import {DocumentObjectInputComponent} from './components/document-object-input/document-object-input.component';
import {EmailInputComponent} from './components/email-input/email-input.component';
import {GeneralDataHandlingService} from '../../../../../services/general.data-handling.service';
import {InputListComponent} from './components/input-list/input-list.component';
import {AddressInputComponent} from './components/address-input/address-input.component';
import {LanguageTextInputComponent} from './components/language-text-input/language-text-input.component';
import {BooleanInputComponent} from './components/boolean-input/boolean-input.component';
import {InputListTextComponent} from './components/input-list-text/input-list-text.component';
import {NameIdInputComponent} from './components/name-id-input/name-id-input.component';
import {UserService} from '../../../../../services/user.service';
import {AssignmentService} from '../../../../../services/assignment.service';
import {PassportDto, StatusStringEnum} from '../../../../../models/Passport';
import {ImageInputComponent} from './components/image-input/image-input.component';
import {UiComponentDTO} from '../../../../../models/Schema';
import {isVisibleIfSatisfied} from '../../../../shared/utils/visible-if';

@Component({
  selector: 'app-form-builder',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TextInputComponent,
    IxModule,
    TranslateModule,
    ReactiveFormsModule,
    EnumInputComponent,
    NumberInputComponent,
    DateInputComponent,
    DocumentInputComponent,
    DocumentObjectInputComponent,
    EmailInputComponent,
    InputListComponent,
    AddressInputComponent,
    LanguageTextInputComponent,
    BooleanInputComponent,
    InputListTextComponent,
    NameIdInputComponent,
    ImageInputComponent,
  ],
  templateUrl: './form-builder.component.html',
  styleUrl: './form-builder.component.scss',
})
export class FormBuilderComponent implements OnInit, OnChanges, OnDestroy {
  readonly location = inject(Location);
  readonly passportService = inject(PassportService);
  readonly userService = inject(UserService);
  readonly assignmentService = inject(AssignmentService);
  readonly generalDataHandlingService = inject(GeneralDataHandlingService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);

  @Input() passportId!: string;
  @Input() selectedCategory = 0;
  @Input() schemaId: string;
  @Input() attributeAssignments: BatteryPassAttributeLabel[] = [];
  @Input() isUserListLoading!: boolean;
  @Input() purpose: FormPurpose = 'edit';
  @Input() isCatenaX = false;
  /** UI components from the schema's `uiSchema.ui:components` array. */
  @Input() uiComponents: UiComponentDTO[] = [];
  @Input() fieldSaveFunction?: (
    value: string | number,
    attribute: FormFieldAttribute,
    control: AbstractControl,
    fullValuePath: string,
  ) => void;
  @Output() expandAssignmentPane = new EventEmitter();
  @Output() cancelClicked = new EventEmitter();
  @Output() saveClicked = new EventEmitter<StatusStringEnum>();
  passportStatus: StatusStringEnum = StatusStringEnum.DRAFT;

  passport?: PassportDto;
  passportIsSaving = false;
  private hasPassportBeenLoaded = false;
  categories: FormSubCategory[] = [];
  protected readonly StatusStringEnum = StatusStringEnum;

  loading = true;

  listTypes = [
    'list',
    'list:document',
    'list:language',
    'chart:bar',
    'chart:pie',
    'chart:line',
  ];

  /** Map of normalized valuePaths to their configured UI components. */
  uiComponentForAttribute: Map<string, UiComponentDTO> = new Map();

  /**
   * O(1) lookup map for attribute assignment labels keyed by attribute valuePath.
   * Rebuilt whenever the `attributeAssignments` input changes.
   */
  private attributeLabelMap: Map<string, BatteryPassAttributeLabel> = new Map();

  readonly passes = this.passportService.passes;
  readonly passportIsSavingSignal = this.passportService.passportIsSaving;

  private changeLangSubscription?: Subscription;

  /**
   * Monotonically increasing counter used to discard stale results from
   * overlapping `reprocessFormForLanguage()` calls when the user switches
   * languages in rapid succession (e.g. en → de → fr).
   */
  private reprocessGeneration = 0;

  constructor() {
    effect(() => {
      const isCurrentlySaving = this.passportIsSavingSignal();
      const currentPassport = this.passes()[this.passportId];

      // Update saving spinner state
      this.passportIsSaving = this.calculatePassportIsSaving(
        this.hasPassportBeenLoaded,
        isCurrentlySaving,
      );

      this.passport = currentPassport;
      if (this.passport && !this.hasPassportBeenLoaded) {
        this.hasPassportBeenLoaded = true;
      }

      this.passportStatus =
        (this.passport?.metadata?.status?.status as StatusStringEnum) ||
        StatusStringEnum.DRAFT;
      this.loading = false;
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedCategory']) {
      this.loading = true;
      this.selectedCategory = changes['selectedCategory'].currentValue;
      this.passport = this.passes()[this.passportId];
      this.loadCategories(this.schemaId)
        .then((schema) => {
          this.categories = schema[this.selectedCategory]?.attributes ?? [];
        })
        .finally(() => {
          this.loading = false;
          this.cdr.markForCheck();
        });
    }
    if (changes['uiComponents']) {
      this.buildUiComponentMap();
      this.cdr.markForCheck();
    }
    if (changes['attributeAssignments']) {
      this.buildAttributeLabelMap();
      this.cdr.markForCheck();
    }
  }

  ngOnInit(): void {
    this.buildAttributeLabelMap();
    this.loadCategories(this.schemaId).then((schema) => {
      this.categories = this.toRenderableSubCategories(
        schema[this.selectedCategory]?.attributes ?? [],
      );
      this.categories = schema[this.selectedCategory]?.attributes ?? [];
    });

    // Re-process the form in the newly selected language once ngx-translate
    // has finished loading it. The language selection/loading itself is
    // already handled elsewhere; here we only react to the resulting event.
    this.changeLangSubscription = this.translate.onLangChange.subscribe(() => {
      this.reprocessFormForLanguage();
    });
  }

  ngOnDestroy(): void {
    this.changeLangSubscription?.unsubscribe();
  }

  /**
   * Reloads and re-maps the schema categories for the currently selected
   * schema/category so that labels reflect the active language, then
   * rebuilds derived lookup maps and triggers change detection for this
   * OnPush component.
   */
  private reprocessFormForLanguage(): void {
    if (!this.schemaId) {
      return;
    }
    this.loading = true;
    this.reprocessGeneration += 1;
    const generation = this.reprocessGeneration;
    this.loadCategories(this.schemaId)
      .then((schema) => {
        // Discard this result if a newer language switch has started in the meantime.
        if (generation !== this.reprocessGeneration) {
          return;
        }
        this.categories = schema[this.selectedCategory]?.attributes ?? [];
        this.buildUiComponentMap();
        this.buildAttributeLabelMap();
      })
      .finally(() => {
        if (generation !== this.reprocessGeneration) {
          return;
        }
        this.loading = false;
        this.cdr.markForCheck();
      });
  }

  async onSubmitClick(status: StatusStringEnum) {
    this.passportStatus = status;
    this.saveClicked.emit(status);
  }

  onCancelClick() {
    this.cancelClicked.emit();
  }

  async loadCategories(schemaId: string): Promise<FormCategory[]> {
    if (this.purpose !== 'assignment') {
      return firstValueFrom(
        this.passportService.loadMappedSchema$(schemaId, this.passportId).pipe(
          catchError((err) => {
            datadogLogs.logger.error('Failed to load categories', {
              schemaId,
              purpose: this.purpose,
              error: err,
            });
            return of([]);
          }),
        ),
      );
    } else {
      const openAttributes = await this.loadOpenAttributes();
      const schema = await firstValueFrom(
        this.passportService.loadMappedSchema$(schemaId, this.passportId).pipe(
          catchError((err) => {
            datadogLogs.logger.error('Failed to load categories', {
              schemaId,
              purpose: this.purpose,
              error: err,
            });
            return of([]);
          }),
        ),
      );
      return this.assignmentService.filterAttributesByAssignments(
        schema,
        openAttributes,
      );
    }
  }

  async loadOpenAttributes(): Promise<BatteryPassAttributeLabel[]> {
    return new Promise((resolve) => {
      let resolved = false;

      const subscription = this.userService.userDataChange.subscribe(
        async (user) => {
          if (resolved) {
            return;
          }

          if (!user?.id) {
            resolved = true;
            resolve([]);
            queueMicrotask(() => subscription?.unsubscribe());
            return;
          }

          const attributes =
            await this.assignmentService.loadAttributeAssignments(
              user.id,
              this.passportId,
            );

          if (resolved) {
            return;
          }

          resolved = true;
          resolve(
            attributes.filter((assignment) => assignment.status !== 'complete'),
          );
          queueMicrotask(() => subscription?.unsubscribe());
        },
      );
    });
  }

  private toRenderableSubCategories(
    attributes: FormSubCategory[],
  ): FormSubCategory[] {
    return attributes.map((entry) => ({
      ...entry,
      attributes: entry.attributes ?? [],
    }));
  }

  getData(valuePath: string): unknown {
    if (!this.passport) {
      return undefined;
    }
    return (
      this.generalDataHandlingService.getValueByPath(
        this.passport.data,
        valuePath,
      ) ?? ''
    );
  }

  /**
   * Returns the attributes to render: those whose ui-schema `visibleIf` rule
   * (if any) is satisfied by the current (in-edit) passport data.
   */
  visibleAttributes(attributes: FormFieldAttribute[]): FormFieldAttribute[] {
    return attributes.filter((attribute) =>
      isVisibleIfSatisfied(attribute.visibleIf, this.passport?.data),
    );
  }

  onAttributeAssignmentClicked(
    label: BatteryPassAttributeLabel,
    attribute: FormFieldAttribute,
  ) {
    label.attributeLabel = attribute.translation?.label;
    this.expandAssignmentPane.emit(label);
  }

  /** Returns the assignment label for the given attribute, or undefined if none exists. */
  getAttributeLabel(
    attribute: FormFieldAttribute,
  ): BatteryPassAttributeLabel | undefined {
    return this.attributeLabelMap.get(attribute.valuePath);
  }

  /**
   * Returns true when the attribute is currently assigned and awaiting a data-provider
   * submission (status 'open'), or when a new unsaved label (no id yet) is present.
   *
   * Note: when no label exists (`undefined`), this returns `false` — attribute is not assigned.
   * The previous implementation had an unreachable `else { return true }` that was a latent bug:
   * it would have incorrectly reported an unassigned attribute as "assigned" if called outside
   * the template guard `@if (getAttributeLabel(attribute); as label)` which already ensures a
   * label exists before this method is invoked.
   */
  isAttributeAssignedTo(attribute: FormFieldAttribute): boolean {
    const label = this.attributeLabelMap.get(attribute.valuePath);
    // A persisted label has an id; it is "assigned to" only while still open (awaiting submission).
    // A brand-new, not-yet-saved label has no id but is considered assigned immediately.
    // No label at all → not assigned.
    if (label?.id) {
      return label.status === 'open';
    } else {
      return label !== undefined;
    }
  }

  /** Returns true when the attribute's assignment has been completed by the data provider. */
  isAttributeSubmittedBy(attribute: FormFieldAttribute): boolean {
    return (
      this.attributeLabelMap.get(attribute.valuePath)?.status === 'complete'
    );
  }

  /**
   * Rebuilds the uiComponentForAttribute map from the current categories and uiComponents.
   * Called after categories load and whenever uiComponents changes so that the template
   * can perform faster map lookups instead of iterating uiComponents on every change detection cycle.
   */
  private buildUiComponentMap(): void {
    const map = new Map<string, UiComponentDTO>();
    for (const subCategory of this.categories) {
      for (const attribute of subCategory.attributes) {
        const normalizedPath = this.normalizePath(attribute.valuePath);
        const component = this.getUiComponentForField(normalizedPath);
        // Only imagePicker uses the map-based rendering; all other
        // component types are handled via the attribute.type overlay.
        if (component && component.type === 'imagePicker') {
          map.set(normalizedPath, component);
        }
      }
    }
    this.uiComponentForAttribute = map;
  }

  /**
   * Rebuilds the O(1) assignment-label lookup map from the current attributeAssignments input.
   * Called in ngOnInit and whenever the attributeAssignments input changes so template
   * lookups (getAttributeLabel, isAttributeAssignedTo, isAttributeSubmittedBy) are O(1).
   */
  private buildAttributeLabelMap(): void {
    this.attributeLabelMap = new Map(
      this.attributeAssignments.map((label) => [label.attribute, label]),
    );
  }

  /**
   * Returns the UI component whose `reference` matches the given
   * full item path, or `undefined` if no component is configured for it.
   *
   * Array indices are stripped before comparison so that references without indices
   * still match runtime paths containing indices.
   */
  private getUiComponentForField(
    fullItemPath: string,
  ): UiComponentDTO | undefined {
    const normalizedPath = this.normalizePath(fullItemPath);
    return this.uiComponents.find(
      (component) => component.reference === normalizedPath,
    );
  }

  private normalizePath(path: string): string {
    return path.replace(/\[\d+\]/g, '');
  }

  // Test helper method to access the effect logic
  calculatePassportIsSaving(
    hasPassportBeenLoaded: boolean,
    isCurrentlySaving: boolean,
  ): boolean {
    return hasPassportBeenLoaded && isCurrentlySaving;
  }
}
